'use client';

import { useEffect } from 'react';
import { usePathname } from '@/i18n/routing';
import { getLastNavDirection, notePopNavigation, reconcileInAppNavigation } from '@/utils/inAppNavigation';

/**
 * Restores the lobby's scroll position on back navigation (forward starts at top).
 * Mount once from ClientLayout. Re-applies the target as the async lobby grows,
 * scrolls instantly so native/Next restorations don't fight it, and yields to input.
 */

export const LOBBY_SCROLL_KEY = 'lobby:scroll-y';

const LOBBY_PATH = '/';

const SETTLE_MS = 1200; // settle window after the layout stops growing
const HARD_CAP_MS = 8000; // never hold the page longer than this
const STABLE_FRAMES = 3;

const SCROLL_KEYS = new Set(['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ', 'Spacebar']);

// Set while restoring so the saver doesn't persist the restore's own transient positions.
let isRestoring = false;

const readKey = (key: string): string | null => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeKey = (key: string, value: string): void => {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // ignore
  }
};

const removeKey = (key: string): void => {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
};

// Drives the window to `targetY` until reached and stable, the layout settles, the
// cap elapses, or the user scrolls. Returns a canceller for effect cleanup.
const restoreScroll = (targetY: number): (() => void) => {
  isRestoring = true;

  const html = document.documentElement;
  const prevScrollBehavior = html.style.scrollBehavior;
  html.style.scrollBehavior = 'auto'; // neutralise `scroll-smooth` for an instant jump

  let cancelled = false;
  let rafId = 0;
  let stable = 0;
  let lastHeight = -1;
  const start = performance.now();
  let deadline = start + SETTLE_MS;
  const listeners = new AbortController();

  const finish = (): void => {
    if (cancelled) {
      return;
    }
    cancelled = true;
    if (rafId) {
      cancelAnimationFrame(rafId);
    }
    listeners.abort();
    html.style.scrollBehavior = prevScrollBehavior;
    isRestoring = false;
  };

  const onUserScroll = (): void => finish(); // yield to real input
  const onKeyDown = (event: KeyboardEvent): void => {
    if (SCROLL_KEYS.has(event.key)) {
      finish();
    }
  };

  const apply = (now: number): void => {
    if (cancelled) {
      return;
    }

    const maxScroll = html.scrollHeight - window.innerHeight;

    // Content still arriving: extend the window so late rows can't park us short.
    if (html.scrollHeight !== lastHeight) {
      lastHeight = html.scrollHeight;
      deadline = now + SETTLE_MS;
    }

    if (Math.abs(window.scrollY - targetY) > 1) {
      window.scrollTo({ top: targetY, left: 0, behavior: 'instant' as ScrollBehavior });
    }

    const reached = maxScroll >= targetY && Math.abs(window.scrollY - targetY) <= 1;
    stable = reached ? stable + 1 : 0;

    if (stable >= STABLE_FRAMES || now >= deadline || now - start >= HARD_CAP_MS) {
      finish();
      return;
    }

    rafId = requestAnimationFrame(apply);
  };

  const { signal } = listeners;
  window.addEventListener('wheel', onUserScroll, { passive: true, signal });
  window.addEventListener('touchstart', onUserScroll, { passive: true, signal });
  window.addEventListener('keydown', onKeyDown, { signal });
  rafId = requestAnimationFrame(apply);

  return finish;
};

export const useLobbyScrollRestoration = (): void => {
  const pathname = usePathname();

  useEffect(() => {
    const handlePopState = () => {
      notePopNavigation();
      reconcileInAppNavigation();
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    if (pathname !== LOBBY_PATH) {
      return;
    }

    let rafId = 0;
    let pending = false;

    const handleScroll = () => {
      if (pending || isRestoring) {
        return;
      }
      pending = true;
      const y = window.scrollY;
      rafId = requestAnimationFrame(() => {
        writeKey(LOBBY_SCROLL_KEY, String(y));
        pending = false;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [pathname]);

  useEffect(() => {
    reconcileInAppNavigation();
    const direction = getLastNavDirection();

    if (pathname !== LOBBY_PATH) {
      return;
    }

    if (direction !== 'back') {
      removeKey(LOBBY_SCROLL_KEY);
      return;
    }

    const savedRaw = readKey(LOBBY_SCROLL_KEY);
    if (savedRaw == null) {
      return;
    }

    const targetY = Number(savedRaw);
    if (!Number.isFinite(targetY) || targetY <= 0) {
      return;
    }

    return restoreScroll(targetY);
  }, [pathname]);
};

export default useLobbyScrollRestoration;
